import { joiResolver } from '@hookform/resolvers/joi';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CancelButton from '@/components/atoms/CancelButton';
import useLocalizedJoi from '@/components/hooks/useLocalizedJoi';
import { useChatVadPresetsQuery } from '@/types/graphql';

export type ChatEnvironment = {
  vadPresetId: string;
};

export type ChatEnvironmentSettingsDialogProps = {
  open: boolean;
  defaultValue: ChatEnvironment;
  chatRoomId: string;
  sessionEntryCode: string;
  userId: string;
  userPassword: string;
  onClose: (value: ChatEnvironment | undefined) => void;
};

const ChatEnvironmentSettingsDialog = (
  props: ChatEnvironmentSettingsDialogProps
) => {
  const { t } = useTranslation('chat');
  const joi = useLocalizedJoi();
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<ChatEnvironment>({
    defaultValues: {
      vadPresetId: props.defaultValue.vadPresetId,
    },
    resolver: joiResolver(
      joi.object<ChatEnvironment>({
        vadPresetId: joi.string().uuid().required(),
      })
    ),
  });

  useEffect(() => {
    if (props.open) {
      setValue('vadPresetId', props.defaultValue.vadPresetId);
    }
  }, [props.open, setValue, props.defaultValue.vadPresetId]);

  const vadPresets = useChatVadPresetsQuery({
    variables: {
      input: {
        chatRoomId: props.chatRoomId,
        sessionEntryCode: props.sessionEntryCode,
        userId: props.userId,
        userPassword: props.userPassword,
      },
    },
    skip: !props.open,
  });
  const vadPresetsLocal = vadPresets.data?.chatVadPresets?.slice();

  return (
    <Dialog open={props.open} scroll="body" maxWidth="xs">
      <DialogTitle>{t('settings.vadInputLabel')}</DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Stack
          justifyContent="center"
          alignItems="center"
          spacing={3}
          sx={{ my: 2 }}
        >
          <Stack spacing={2} sx={{ my: 1, width: '100%' }}>
            <Controller
              name="vadPresetId"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id="vad-preset-label">
                    {t('settings.vadInputLabel')}
                  </InputLabel>
                  <Select
                    labelId="vad-preset-label"
                    label={t('settings.vadInputLabel')}
                    sx={{ textAlign: 'left' }}
                    {...field}
                  >
                    {vadPresetsLocal
                      ?.sort((a) => (a.default ? -1 : 1))
                      .map((x) => (
                        <MenuItem key={x.id} value={x.id}>
                          {x.label}
                        </MenuItem>
                      )) ?? []}
                  </Select>
                  <FormHelperText
                    sx={{ color: (t) => t.palette.error.main }}
                    error={errors.vadPresetId?.message != null}
                  >
                    {errors.vadPresetId?.message ?? ''}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          gap={1}
          width={1}
        >
          <CancelButton onClick={() => props.onClose(undefined)}></CancelButton>
          <Button
            onClick={handleSubmit(
              async (input) => {
                props.onClose({
                  vadPresetId: input.vadPresetId,
                });
              },
              (err) => {
                console.log(err);
              }
            )}
          >
            OK
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default ChatEnvironmentSettingsDialog;
